import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { hvoFirestore, vceFirestore } from "../../configs/FirebaseConfig";
import { productionProjectId } from "./productionOrders";

/**
 * Writing test orders into the dev projects (Bestellingen → Testdata), with the CMS's own session
 * and through the CMS rules, like scripts/copy-orders does. Never production: every call checks the
 * project it would write to against production's.
 */

// Reads and writes at a time; a delivery day is at most a few hundred orders
const PARALLEL = 10;

const devFirestore = (shop) => {
  const db = shop === "HVO" ? hvoFirestore : vceFirestore;
  const projectId = db.app.options.projectId;
  if (!projectId || projectId === productionProjectId(shop)) {
    throw new Error(`${shop}: schrijven naar ${projectId || "een onbekend project"} geweigerd, dat is productie`);
  }
  return db;
};

const inParallel = async (items, task) => {
  const results = [];
  for (let index = 0; index < items.length; index += PARALLEL) {
    results.push(...(await Promise.all(items.slice(index, index + PARALLEL).map(task))));
  }
  return results;
};

/** A reference read from production points into production; in dev it means the same path there. */
const rebase = (value, db) => {
  if (value instanceof firebase.firestore.DocumentReference) return db.doc(value.path);
  if (Array.isArray(value)) return value.map((item) => rebase(item, db));
  if (value && typeof value === "object" && Object.getPrototypeOf(value) === Object.prototype) {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, rebase(item, db)]));
  }
  return value;
};

/** The project the test orders of this shop go to, e.g. vleescentrum-dev. */
export const devProjectId = (shop) => devFirestore(shop).app.options.projectId;

/** Which of these order ids the shop's dev project already has. */
export const existingDevOrderIds = async (shop, ids) => {
  const db = devFirestore(shop);
  const found = await inParallel(ids, async (id) => ((await db.collection("orders").doc(id).get()).exists ? id : null));
  return new Set(found.filter(Boolean));
};

/**
 * Create the planned orders in the shop's dev project: as CREATED - the only way the rules allow -
 * and then with their production status.
 *
 * @param {Array<object>} entries - the "create" entries of planDevCopy
 * @returns {Promise<{written: number, failed: string[]}>}
 */
export const writeDevOrders = async (shop, entries) => {
  const db = devFirestore(shop);
  const failed = [];
  let written = 0;
  await inParallel(entries, async (entry) => {
    const reference = db.collection("orders").doc(entry.id);
    try {
      await reference.set(rebase(entry.data, db));
      if (entry.finalStatus) await reference.update({ orderStatus: entry.finalStatus });
      written += 1;
    } catch (error) {
      failed.push(`${entry.orderId || entry.id}: ${error.code || error.message}`);
    }
  });
  return { written, failed };
};
